import React from "react";
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import defaultProfile from '../../../../assets/images/defaultProfile.png';
import { getFullUrl } from '../../../../utils/url';

interface Recognition {
  id: number;
  senderId: string;
  senderName: string;
  senderAvatar: string | null;
  receiverId: string;
  receiverName: string;
  receiverAvatar: string | null;
  message: string;
  values: string[];
  timestamp: Date | string;
  type?: 'recognition' | 'feedback' | 'coaching' | 'concern';
}

interface RecognitionCardProps {
  recognition: Recognition;
  currentUserId?: string;
  onLike?: (id: string) => void;
  onComment?: (id: string, comment: string) => void;
}

// Helper function to get avatar URL
const getAvatarUrl = (avatarUrl?: string | null): string => {
  if (!avatarUrl || avatarUrl === "") {
    return defaultProfile;
  }

  // If it's already a full URL (starts with http/https), return as-is
  return getFullUrl(avatarUrl) || defaultProfile;
};

const RecognitionCard: React.FC<RecognitionCardProps> = ({
  recognition,
  currentUserId,
  onLike,
  onComment,
}) => {
  const { t } = useTranslation('performance');

  const formatTimeAgo = (timestamp: Date | string) => {
    const now = new Date().getTime();
    const time = new Date(timestamp).getTime();
    const diff = now - time;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return t('recognition.minutesAgo', { count: minutes });
    if (hours < 24) return t('recognition.hoursAgo', { count: hours });
    return t('recognition.daysAgo', { count: days });
  };

  const getValueColor = (value: string): { bg: string; text: string; border: string } => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      'Innovation': { bg: '#f3e8ff', text: '#7c3aed', border: '#e9d5ff' },
      'Collaboration': { bg: '#dbeafe', text: '#2563eb', border: '#bfdbfe' },
      'Excellence': { bg: '#d1fae5', text: '#059669', border: '#a7f3d0' },
      'Integrity': { bg: '#fef3c7', text: '#d97706', border: '#fde68a' },
      'Customer Focus': { bg: '#fee2e2', text: '#dc2626', border: '#fecaca' },
      'Growth Mindset': { bg: '#dbeafe', text: '#2563eb', border: '#bfdbfe' }
    };
    return colors?.[value] || { bg: '#e5e7eb', text: '#6b7280', border: '#d1d5db' };
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const isFeedback = recognition.type === 'feedback' || recognition.type === 'coaching' || recognition.type === 'concern';
  const isCoaching = recognition.type === 'coaching';
  const isConcern = recognition.type === 'concern';

  const getBorderColor = (type?: string) => {
    if (type === 'feedback' || type === 'coaching' || type === 'concern') return '#0ea5b8';
    return '#6d28d9';
  };

  return (
    <div
      className="bg-card border-s-[6px] rounded-lg p-3 mb-2 shadow-sm max-w-3xl mx-auto w-full relative"
      style={{ borderColor: getBorderColor(recognition.type) }}
    >
      {/* Type Badge (gradient) */}
      <div
        className={`absolute top-3 end-3 flex items-center gap-1 px-2.5 py-1 rounded-lg text-white ${
          isFeedback
            ? 'bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_4px_12px_rgba(6,182,212,0.12)]'
            : 'bg-gradient-to-r from-violet-600 to-pink-500 shadow-[0_4px_12px_rgba(124,58,237,0.12)]'
        }`}
      >
        {isCoaching ? <AppIcon name="Lightbulb" size={14} /> :
         isConcern ? <AppIcon name="AlertTriangle" size={14} /> :
         isFeedback ? <AppIcon name="MessageCircle" size={14} /> :
         <AppIcon name="Heart" size={14} />}
        <span className="text-[0.68rem] font-bold text-white">
          {isCoaching ? t('recognition.coaching') : isConcern ? t('recognition.concern') : isFeedback ? t('recognition.feedback') : t('recognition.recognition')}
        </span>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3 mb-2 pe-10">
        {/* Sender */}
        {recognition.senderAvatar ? (
          <img
            src={getAvatarUrl(recognition.senderAvatar)}
            alt={recognition.senderName}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">
            {getInitials(recognition.senderName)}
          </div>
        )}
        <span className="font-medium text-foreground text-sm">
          {recognition.senderName}
        </span>

        <AppIcon name="ArrowRight" size={14} className="text-muted-foreground" />

        {/* Recipient */}
        {recognition.receiverAvatar ? (
          <img
            src={getAvatarUrl(recognition.receiverAvatar)}
            alt={recognition.receiverName}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">
            {getInitials(recognition.receiverName)}
          </div>
        )}
        <span className="font-medium text-foreground text-sm">
          {recognition.receiverName}
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Timestamp */}
        <span className="text-muted-foreground text-xs">
          {formatTimeAgo(recognition.timestamp)}
        </span>
      </div>

      {/* Message */}
      <p className="mb-2 leading-relaxed text-foreground text-sm">
        {recognition.message}
      </p>

      {/* Values - Only show for recognition */}
      {!isFeedback && recognition.values && recognition.values.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {recognition.values.map((value, index) => {
            const { bg, text, border } = getValueColor(value);
            return (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: bg,
                  color: text,
                  border: `1px solid ${border}`,
                }}
              >
                {value}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecognitionCard;